import React, { useCallback } from 'react';
import Image from 'next/image';
import {
  Box,
  Stack,
  Typography,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { Button } from '@/components/Button/Button';
import type { StatItem } from './helpers';

type Props = {
  item: StatItem;
  onReadReview: (value: StatItem | null) => void;
};

const Stat = ({ item, onReadReview }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleReadReview = useCallback(() => {
    onReadReview?.(item);
  }, [item, onReadReview]);

  const button = (
    <Button
      small
      sx={{
        width: '100%',
        minWidth: 'fit-content',
      }}
      variant="contained"
      onClick={handleReadReview}
    >
      Read Full Review
    </Button>
  );

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      flexWrap="wrap"
      flex={{ xs: '1 0 343px', sm: '1 0 567px' }}
      sx={{
        position: 'relative',
        borderRadius: theme.spacing(4),
        overflow: 'hidden',
        boxShadow: '7px 8px 30px 0px #00000014',
        backgroundColor: theme.palette.common.white,

        '&::before': {
          content: '""',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          position: 'absolute',
          border: '1px solid #00000011',
          borderRadius: theme.spacing(4),
          pointerEvents: 'none',
        },
      }}
    >
      <Stack
        width={{ xs: '100%', md: '50%' }}
        sx={{
          padding: {
            xs: theme.spacing(3, 5, 2),
            md: theme.spacing(4, 4.5, 7.5, 6),
          },
        }}
        justifyContent="flex-start"
      >
        <Stack
          sx={{
            paddingBottom: theme.spacing(2.5),
            borderBottom: '1px solid #717792',
          }}
        >
          <Stack
            sx={{
              gap: theme.spacing(4),
              flexDirection: 'row',
              marginBottom: theme.spacing(2),
            }}
          >
            <Box
              position="relative"
              height={item.user.avatar.height}
              width={item.user.avatar.width}
            >
              <Image
                src={`/images/home/${item.user.avatar.src}`}
                layout="fill"
                objectPosition="center"
              />
            </Box>

            <Box
              position="relative"
              height={item.user.companyLogo.height}
              width={item.user.companyLogo.width}
              sx={{
                marginTop: theme.spacing(1.5),
              }}
            >
              <Image
                src={`/images/home/${item.user.companyLogo.src}`}
                layout="fill"
                objectPosition="center"
              />
            </Box>
          </Stack>

          <Stack
            sx={{
              gap: theme.spacing(1),
              marginTop: theme.spacing(2.5),
              paddingLeft: theme.spacing(1),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: `${theme.spacing(4.75)} !important`,
                fontWeight: '700',
                lineHeight: `1.15789474 !important`,
                color: '#52545F',
              }}
            >
              {item.user.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: `${theme.spacing(3)} !important`,
                fontWeight: '700',
                lineHeight: `1.63 !important`,
                color: theme.palette.primary.main,
              }}
            >
              {item.user.position}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          sx={{
            py: '9px',
            px: '16px',
            marginTop: theme.spacing(7),
            borderRadius: theme.spacing(3),
            backgroundColor: '#E5EDF8',
            position: 'relative',
            '&::before': {
              content: '""',
              top: '-18px',
              left: '16px',
              position: 'absolute',
              width: '0',
              height: '0',
              borderBottom: '20px solid #E5EDF8',
              borderRight: '18px solid transparent',
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#34353E',
              fontSize: `${theme.spacing(3)} !important`,
              lineHeight: `1.63 !important`,
            }}
          >
            {item.shortReview}
          </Typography>
        </Stack>

        {!isMobile && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              marginTop: 'auto',
              paddingTop: theme.spacing(5),
            }}
          >
            {button}
          </Box>
        )}
      </Stack>

      <Stack
        sx={{
          rowGap: { xs: 0, lg: theme.spacing(6) },
          width: { xs: '100%', md: '50%' },
          color: {
            xs: theme.palette.common.black,
            md: theme.palette.common.white,
          },
          backgroundColor: { xs: theme.palette.common.white, md: '#717792' },
          padding: {
            xs: theme.spacing(0, 6),
            md: theme.spacing(5, 6, 3, 7),
          },
        }}
      >
        <Stack
          sx={{
            gap: theme.spacing(1.5),
            py: { xs: theme.spacing(2), md: 0 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: `${theme.spacing(4.5)} !important`,
              lineHeight: `1.46 !important`,
            }}
          >
            Solution:
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: `${theme.spacing(3)} !important`,
              lineHeight: `1.63 !important`,
            }}
          >
            {item.solution}
          </Typography>
        </Stack>

        <Stack
          sx={{
            gap: theme.spacing(1.5),
            py: { xs: theme.spacing(2), md: 0 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: `${theme.spacing(4.5)} !important`,
              lineHeight: `1.46 !important`,
            }}
          >
            Results:
          </Typography>

          <List
            disablePadding
            component="ul"
            sx={{
              listStyle: 'none',
              gap: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {item.results.map((result) => (
              <ListItem
                key={result}
                component="li"
                disablePadding
                sx={{
                  position: 'relative',
                  display: 'flex',
                  gap: theme.spacing(1.7),
                  fontSize: `${theme.spacing(3)} !important`,
                  lineHeight: `1.63 !important`,
                  '&::before': {
                    content: '""',
                    top: '8px',
                    left: '-14px',
                    position: 'absolute',
                    width: '6.55px',
                    height: '6.55px',
                    borderRadius: theme.spacing(1.64),
                    backgroundColor: {
                      xs: theme.palette.primary.main,
                      md: '#EEF6F6',
                    },
                  },
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: result,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      </Stack>

      {isMobile && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            padding: theme.spacing(2, 5, 4),
          }}
        >
          {button}
        </Box>
      )}
    </Stack>
  );
};

export default Stat;
